import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FaCheckCircle, FaTimesCircle, FaEye, FaUser, FaMapMarkerAlt, FaEuroSign } from 'react-icons/fa';

export default function RentalApprovals() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [pendingProducts, setPendingProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (!user || !user.roles?.includes('admin')) {
      navigate('/');
      return;
    }
    fetchPendingProducts();
  }, [user]);

  const fetchPendingProducts = async () => {
    try {
      // Use the new admin-only endpoint that returns pending rental products
      const response = await axios.get(`${API}/admin/rental-listings/pending`);
      
      console.log(`[Rental Approvals] Found ${response.data.length} pending rental products`);
      setPendingProducts(response.data);
    } catch (error) {
      console.error('Error fetching pending products:', error);
      toast.error('Erreur lors du chargement des annonces');
    } finally {
      setLoading(false);
    }
  };

  const approveProduct = async (productId) => {
    try {
      await axios.post(`${API}/products/${productId}/approve`);
      toast.success('Annonce approuvée et publiée');
      fetchPendingProducts();
    } catch (error) {
      console.error('Error approving product:', error);
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const openRejectModal = (product) => {
    setSelectedProduct(product);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const rejectProduct = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Veuillez indiquer une raison');
      return;
    }

    try {
      await axios.post(`${API}/products/${selectedProduct.id}/reject`, null, {
        params: { reason: rejectionReason }
      });
      toast.success('Annonce rejetée');
      setShowRejectModal(false);
      fetchPendingProducts();
    } catch (error) {
      console.error('Error rejecting product:', error);
      toast.error('Erreur lors du rejet');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-xl text-[#0A2540]">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-2">
            Approbation des Annonces de Location
          </h1>
          <p className="text-gray-600">
            Validez les annonces de location créées par les fournisseurs
          </p>
        </div>

        {/* Stats */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-sm text-gray-600">Annonces en attente</p>
                <p className="text-4xl font-bold text-[#00D4FF]">{pendingProducts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Products List */}
        {pendingProducts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FaCheckCircle className="mx-auto text-6xl text-[#635BFF] mb-4" />
              <p className="text-gray-500 text-lg">
                Aucune annonce en attente d'approbation
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {pendingProducts.map((product) => (
              <Card key={product.id} className="hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Image */}
                    <div className="lg:w-1/3">
                      <img
                        src={product.images && product.images.length > 0 ? product.images[0] : '/placeholder-equipment.jpg'}
                        alt={i18n.language === 'fr' ? product.name_fr : product.name_en}
                        className="w-full h-64 object-cover rounded-lg"
                      />
                    </div>

                    {/* Details */}
                    <div className="lg:w-2/3">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h2 className="text-2xl font-bold text-[#0A2540] mb-2">
                            {i18n.language === 'fr' ? product.name_fr : product.name_en}
                          </h2>
                          <Badge className="bg-yellow-500 text-white">
                            En attente d'approbation
                          </Badge>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-[#00D4FF]">
                            {product.price_rental}€
                          </div>
                          <div className="text-sm text-gray-500">par jour</div>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="mb-4">
                        <h3 className="font-semibold mb-2">Description (FR):</h3>
                        <p className="text-gray-700">{product.description_fr}</p>
                      </div>

                      <div className="mb-4">
                        <h3 className="font-semibold mb-2">Description (EN):</h3>
                        <p className="text-gray-700">{product.description_en}</p>
                      </div>

                      {/* Info Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                        <div className="flex items-center gap-2 text-sm">
                          <FaUser className="text-blue-500" />
                          <span><strong>Fournisseur ID:</strong> {product.created_by_user_id}</span>
                        </div>
                        {product.location && (
                          <div className="flex items-center gap-2 text-sm">
                            <FaMapMarkerAlt className="text-red-500" />
                            <span><strong>Localisation:</strong> {product.location}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm">
                          <FaEuroSign className="text-[#635BFF]" />
                          <span><strong>Stock:</strong> {product.stock_rental}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-3">
                        <Button
                          onClick={() => approveProduct(product.id)}
                          className="bg-[#635BFF] hover:bg-[#5248d9] flex-1"
                        >
                          <FaCheckCircle className="mr-2" /> Approuver et Publier
                        </Button>
                        <Button
                          onClick={() => openRejectModal(product)}
                          variant="outline"
                          className="text-red-600 border-red-600 hover:bg-red-50 flex-1"
                        >
                          <FaTimesCircle className="mr-2" /> Rejeter
                        </Button>
                        <Button
                          onClick={() => navigate(`/rental/${product.id}`)}
                          variant="outline"
                        >
                          <FaEye className="mr-2" /> Prévisualiser
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Reject Modal */}
        <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl text-[#0A2540]">
                Rejeter l'annonce
              </DialogTitle>
            </DialogHeader>

            {selectedProduct && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded">
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Produit:</strong> {i18n.language === 'fr' ? selectedProduct.name_fr : selectedProduct.name_en}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Fournisseur:</strong> {selectedProduct.created_by_user_id}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Raison du rejet (sera envoyée au fournisseur) *
                  </label>
                  <Textarea
                    rows={6}
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Ex: La description n'est pas assez détaillée. Veuillez ajouter les spécifications techniques complètes..."
                  />
                </div>

                <div className="bg-yellow-50 p-4 rounded">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Le fournisseur sera notifié du rejet et pourra modifier son annonce pour la soumettre à nouveau.
                  </p>
                </div>

                <div className="flex gap-3 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setShowRejectModal(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={rejectProduct}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <FaTimesCircle className="mr-2" /> Confirmer le Rejet
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
